/**
 * nicemail.ca.
 *
 * Boot order matters: the theme is applied before anything renders, so there is
 * never a flash of the wrong palette, and the list draws from IndexedDB before
 * the relay is contacted, so there is no loading state on a client that already
 * holds the mail.
 */
import "./ui/app.css";
import { initTheme, onThemeChange, selectTheme } from "./theme/apply";
import { THEMES } from "./theme/themes";
import { restoreSession, takeRestoreFailure, signOut, type Session } from "./session";
import { renderSignIn } from "./ui/signin";
import { renderLanding } from "./ui/landing";
import { renderApp, releaseApp } from "./ui/shell";

const root = document.querySelector<HTMLDivElement>("#app");
if (!root) throw new Error("#app is missing from index.html");

initTheme();

function themePicker(onChange: () => void): HTMLSelectElement {
  const select = document.createElement("select");
  select.setAttribute("aria-label", "Theme");
  for (const theme of THEMES) {
    const option = document.createElement("option");
    option.value = theme.id;
    option.textContent = theme.name;
    option.title = theme.thesis;
    select.append(option);
  }
  select.value = document.documentElement.dataset["theme"] ?? "index";
  select.addEventListener("change", () => {
    selectTheme(select.value);
    onChange();
  });
  return select;
}

async function start(): Promise<void> {
  // Booting again means the previous app is gone; its sync loop has to go with
  // it, because signing out ends here and never reaches `renderApp`.
  releaseApp();
  // Reconnecting to a phone signer is a network round-trip, so say so rather
  // than flashing the sign-in screen and replacing it a moment later.
  root!.replaceChildren(
    Object.assign(document.createElement("div"), {
      className: "booting",
      textContent: "Reconnecting\u2026",
    }),
  );

  const session = await restoreSession();
  if (session) {
    void show(session);
    return;
  }
  // A stored session that failed belongs to a returning user, so skip the pitch.
  const failure = takeRestoreFailure();
  if (failure) {
    signIn(failure);
    return;
  }
  // A visitor with nothing to sign in to was being shown a key field and a QR
  // code. They get the pitch; sign-in is one click away for the people it is
  // actually for.
  renderLanding(root!, themePicker(() => void start()), { onSignIn: () => signIn() });
}

function signIn(restoreFailure?: string): void {
  renderSignIn(root!, themePicker(() => void start()), (next) => void show(next), restoreFailure);
}

async function show(session: Session): Promise<void> {
  await renderApp(root!, session, {
    // No callback: the picker calls `selectTheme`, which announces the change to
    // the shell. Re-entering `show` here would repaint by building another
    // poller, and that poller syncs on its first run.
    themePicker: () => themePicker(() => undefined),
    onThemeChange,
    onSignOut: async () => {
      await signOut();
      await start();
    },
  });
}

void start();
